import {
  Card,
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { camelCase } from 'lodash'
import { DateTime } from 'luxon'
import { ReactNode } from 'react'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Pie, PieChart, XAxis, YAxis } from 'recharts'
import { match } from 'ts-pattern'
import { Color } from '~/types/colors'
import { cn, formatters } from '../../../utils'
import { chartTypes } from '../types'
import { RatioMetric, isRatioMetric, type Resolution } from './ChartDataProvider'

// Utility function to process data for area/bar charts, extracting values from RatioMetric objects
function processChartDataForDisplay<T extends Record<string, number | Date | RatioMetric>>(
  data: ({ timestamp: Date } & T)[]
): ({ timestamp: Date } & Record<string, number>)[] {
  return data.map((d) => {
    const processed: any = { timestamp: d.timestamp }

    for (const [key, value] of Object.entries(d)) {
      if (key === 'timestamp') continue

      if (isRatioMetric(value)) {
        processed[key] = value.denominator > 0 ? value.numerator / value.denominator : 0
      } else {
        processed[key] = value
      }
    }

    return processed
  })
}

// Utility function to aggregate data for metric/donut/pie charts, handling RatioMetric objects
function aggregateChartData<T extends Record<string, number | Date | RatioMetric>>(
  data: ({ timestamp: Date } & T)[],
  categories: (keyof T)[]
) {
  const reducedData = data.reduce(
    (acc, d) => {
      for (const key of categories) {
        const currentValue = d[key]

        if (isRatioMetric(currentValue)) {
          // It's a RatioMetric object, track numerator and denominator separately
          if (!acc[key] || typeof acc[key] === 'number') {
            acc[key] = { numerator: 0, denominator: 0 }
          }
          const avgAcc = acc[key] as RatioMetric
          avgAcc.numerator += currentValue.numerator
          avgAcc.denominator += currentValue.denominator
        } else {
          // For regular metrics, sum the values
          const currentValue = Number(d[key] ?? 0)
          if (!isNaN(currentValue)) {
            acc[key] = Number(acc[key] ?? 0) + currentValue
          }
        }
      }
      return acc
    },
    {} as Record<keyof T, number | RatioMetric>
  )

  return Object.entries(reducedData).map(([key, value]) => {
    if (isRatioMetric(value)) {
      return {
        name: key,
        value: value.denominator > 0 ? value.numerator / value.denominator : 0,
      }
    } else {
      return {
        name: key,
        value: Number(value),
      }
    }
  })
}

function getYAxisDataKey<T extends Record<string, number | Date | RatioMetric>>(
  data: ({ timestamp: Date } & T)[],
  categories: (keyof T)[]
): string {
  const reducedData = aggregateChartData(data, categories)
    .map(({ name, value }) => ({
      name,
      value: typeof value === 'number' ? Number(value.toFixed(3)) : value,
    }))
    .filter((d) => categories.includes(d.name))
    .sort((a, b) => b.value - a.value)
  return reducedData[0]?.name ?? String(categories[0])
}

type Props<T extends Record<string, number | Date | RatioMetric>> = {
  categories: (keyof T)[]
  data: ({ timestamp: Date } & T)[]
  type: (typeof chartTypes)[number]
  className?: string
  showLegend?: boolean
  children?: ReactNode
  resolution?: Resolution
}

export const chartColorNames = [
  'blue',
  'amber',
  'red',
  'green',
  'orange',
  'purple',
  'fuchsia',
  'teal',
  'lime',
  'pink',
] as const satisfies Color[]

export const Chart = <T extends Record<string, number | Date | RatioMetric>>({
  children,
  className,
  showLegend = true,
  categories,
  resolution,
  ...props
}: Props<T>) => {
  const chartConfig = categories.reduce<ChartConfig>((acc, category, index) => {
    acc[camelCase(String(category))] = {
      label: String(category),
      color: `oklch(var(--color-${chartColorNames[index % chartColorNames.length]}-400))`,
    }
    return acc
  }, {} satisfies ChartConfig)
  return (
    <Card className={cn('h-full w-full bg-white py-4 dark:bg-neutral-50', className)}>
      <div className="flex h-full w-full flex-col gap-4 px-2">
        {children}
        {match({ categories, ...props })
          .when(
            ({ data }) => data.length === 0,
            () => <EmptyChartState>No data available</EmptyChartState>
          )
          .when(
            ({ categories }) => categories.length === 0,
            () => <EmptyChartState>No data source selected</EmptyChartState>
          )
          .with({ type: 'area' }, ({ data, categories }) => {
            const processedData = processChartDataForDisplay(data)
            const parsedData = processedData.map((d) => ({
              ...d,
              timestamp: formatDate(d.timestamp, resolution ?? 'month'),
            }))

            const yAxisDataKey = getYAxisDataKey(data, categories)
            return (
              <>
                {showLegend ? <ChartLegend categories={categories as string[]} /> : <div className="h-4"></div>}
                <ChartContainer config={chartConfig} className="aspect-auto h-full duration-300">
                  <AreaChart accessibilityLayer data={parsedData}>
                    <defs>
                      {categories.map((category) => (
                        <linearGradient
                          key={String(category)}
                          id={`fill${camelCase(String(category))}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={`var(--color-${camelCase(String(category))})`}
                            stopOpacity={0.9}
                          />
                          <stop
                            offset="95%"
                            stopColor={`var(--color-${camelCase(String(category))})`}
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid vertical={false} />

                    <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                    <XAxis
                      dataKey="timestamp"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      minTickGap={32}
                      tickFormatter={(value) => {
                        const date = new Date(value)
                        return date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      }}
                    />
                    <YAxis
                      dataKey={yAxisDataKey}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                      tickMargin={8}
                      minTickGap={32}
                      domain={[0, 'dataMax']}
                      tickFormatter={(value) => {
                        return formatters.number(value)
                      }}
                    />
                    {categories.map((category) => (
                      <Area
                        key={String(category)}
                        dataKey={String(category)}
                        type="monotone"
                        fill={`url(#fill${camelCase(String(category))})`}
                        stroke={`var(--color-${camelCase(String(category))})`}
                      />
                    ))}
                  </AreaChart>
                </ChartContainer>
              </>
            )
          })
          .with({ type: 'bar' }, ({ data, categories }) => {
            const processedData = processChartDataForDisplay(data)
            const parsedData = processedData.map((d) => ({
              ...d,
              timestamp: formatDate(d.timestamp, resolution ?? 'month'),
            }))

            const yAxisDataKey = getYAxisDataKey(data, categories)
            return (
              <>
                {showLegend ? <ChartLegend categories={categories as string[]} /> : <div className="h-4"></div>}
                <ChartContainer config={chartConfig} className="aspect-auto h-full duration-300">
                  <BarChart accessibilityLayer data={parsedData}>
                    <CartesianGrid vertical={false} />

                    <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                    <XAxis
                      dataKey="timestamp"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      minTickGap={32}
                      tickFormatter={(value) => {
                        const date = new Date(value)
                        return date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      }}
                    />
                    <YAxis
                      dataKey={yAxisDataKey}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                      tickMargin={8}
                      minTickGap={32}
                      domain={[0, 'dataMax']}
                      tickFormatter={(value) => {
                        return formatters.number(value)
                      }}
                    />
                    {categories.map((category) => (
                      <Bar
                        key={String(category)}
                        name={camelCase(String(category))}
                        dataKey={String(category)}
                        fill={`var(--color-${camelCase(String(category))})`}
                      />
                    ))}
                  </BarChart>
                </ChartContainer>
              </>
            )
          })
          .with({ type: 'donut' }, ({ data, categories }) => {
            const filteredData = aggregateChartData(data, categories).map(({ name, value }) => ({
              fill: `var(--color-${camelCase(String(name))})`,
              name,
              value: typeof value === 'number' ? Number(value.toFixed(3)) : value,
            }))

            return (
              <>
                {showLegend ? <ChartLegend categories={categories as string[]} /> : <div className="h-4"></div>}
                <ChartContainer config={chartConfig} className="aspect-auto h-full duration-300">
                  <PieChart accessibilityLayer>
                    <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                    <Pie data={filteredData} dataKey={'value'} nameKey={'name'} innerRadius={20} />
                  </PieChart>
                </ChartContainer>
              </>
            )
          })
          .with({ type: 'pie' }, ({ data, categories }) => {
            const filteredData = aggregateChartData(data, categories).map(({ name, value }) => ({
              fill: `var(--color-${camelCase(String(name))})`,
              name,
              value: typeof value === 'number' ? Number(value.toFixed(3)) : value,
            }))

            return (
              <>
                {showLegend ? <ChartLegend categories={categories as string[]} /> : <div className="h-4"></div>}
                <ChartContainer config={chartConfig} className="aspect-auto h-full duration-300">
                  <PieChart accessibilityLayer>
                    <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                    <Pie data={filteredData} dataKey={'value'} nameKey={'name'} />
                  </PieChart>
                </ChartContainer>
              </>
            )
          })
          .with({ type: 'spark_bar' }, ({ data, categories }) => {
            const processedData = processChartDataForDisplay(data)
            const parsedData = processedData.map((d) => ({
              ...d,
              timestamp: formatDate(d.timestamp, resolution ?? 'month'),
            }))

            return (
              <>
                {showLegend ? <ChartLegend categories={categories as string[]} /> : <div className="h-4"></div>}
                <ChartContainer config={chartConfig} className="aspect-auto h-full duration-300">
                  <BarChart accessibilityLayer data={parsedData}>
                    <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                    {categories.map((category) => (
                      <Bar
                        key={String(category)}
                        dataKey={String(category)}
                        fill={`var(--color-${camelCase(String(category))})`}
                      />
                    ))}
                  </BarChart>
                </ChartContainer>
              </>
            )
          })
          .with({ type: 'spark_area' }, ({ data, categories }) => {
            const processedData = processChartDataForDisplay(data)
            const parsedData = processedData.map((d) => ({
              ...d,
              timestamp: formatDate(d.timestamp, resolution ?? 'month'),
            }))

            return (
              <>
                {showLegend ? <ChartLegend categories={categories as string[]} /> : <div className="h-4"></div>}
                <ChartContainer config={chartConfig} className="aspect-auto h-full duration-300">
                  <AreaChart accessibilityLayer data={parsedData}>
                    <defs>
                      {categories.map((category) => (
                        <linearGradient
                          key={String(category)}
                          id={`fill${camelCase(String(category))}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={`var(--color-${camelCase(String(category))})`}
                            stopOpacity={0.9}
                          />
                          <stop
                            offset="95%"
                            stopColor={`var(--color-${camelCase(String(category))})`}
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                      ))}
                    </defs>
                    <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                    {categories.map((category) => (
                      <Area
                        key={String(category)}
                        dataKey={String(category)}
                        type="monotone"
                        fill={`url(#fill${camelCase(String(category))})`}
                        stroke={`var(--color-${camelCase(String(category))})`}
                      />
                    ))}
                  </AreaChart>
                </ChartContainer>
              </>
            )
          })
          .with({ type: 'metric' }, ({ data, categories }) => {
            const filteredData = aggregateChartData(data, categories)
              .map(({ name, value }) => ({
                name,
                value: typeof value === 'number' ? Number(value.toFixed(3)) : value,
              }))
              .filter((d) => categories.includes(d.name))
            return (
              <div
                className={cn('mx-auto grid h-full items-center gap-4', {
                  '@[18rem]:grid-cols-2': categories.length >= 2,
                  '@xl:grid-cols-3': categories.length >= 3,
                })}
              >
                {filteredData.map(({ name, value }, index) => (
                  <div key={name} className="flex min-w-0 flex-col px-4 text-2xl">
                    <div className="flex items-center gap-1">
                      <div
                        className={`bg-${chartColorNames[index % chartColorNames.length]}-400 size-3 flex-none rounded-sm`}
                      />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate text-[0.7em] text-muted-foreground">{name}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <span className="font-bold">{value}</span>
                  </div>
                ))}
              </div>
            )
          })
          .otherwise(() => (
            <EmptyChartState>Chart type not supported</EmptyChartState>
          ))}
      </div>
    </Card>
  )
}

const EmptyChartState = ({ children }: { children: ReactNode }) => (
  <div className="flex size-full items-center justify-center rounded-sm border border-dashed border-neutral-200">
    <span className="text-center text-sm text-gray-500">{children}</span>
  </div>
)

const ChartLegend = ({ categories }: { categories: string[] }) => {
  return (
    <div className="flex w-full justify-end gap-2 px-4">
      {categories.map((category, index) => (
        <div key={category} className="flex items-center gap-2">
          <div className={`bg-${chartColorNames[index % chartColorNames.length]}-400 size-2 flex-none rounded-sm`} />
          <span className="text-xs text-muted-foreground">{category}</span>
        </div>
      ))}
    </div>
  )
}

function formatDate(timestamp: Date, resolution: Resolution) {
  const dt = DateTime.fromJSDate(timestamp)
  switch (resolution) {
    case 'hour':
      return dt.toFormat('LLL d, HH:mm')
    case 'day':
      return dt.toFormat('LLL d, yyyy')
    case 'week':
      return dt.toFormat('LLL d, yyyy')
    case 'month':
      return dt.toFormat('LLL yyyy')
    default:
      return dt.toFormat('LLL d, yyyy')
  }
}
